import { parse } from 'csv-parse/browser/esm/sync';

import type { CsvEntity, ValidationIssue, ValidationReason } from '@royaltyflow/validation';

export const REASONS: readonly ValidationReason[] = [
  'required_field',
  'invalid_type',
  'invalid_value',
  'invalid_date',
  'invalid_date_range',
  'currency_mismatch',
  'csv_malformed',
  'csv_missing_header',
  'csv_duplicate_header',
  'csv_empty',
  'csv_limit_exceeded',
  'reference_not_found',
  'no_sales',
  'share_pool_exceeded',
  'share_unmatched',
  'residual_unmatched',
  'cost_unmatched',
  'sale_crosses_reporting_period',
  'approved_period_overlap',
  'approved_period_unknown',
  'simulation_not_computed',
  'simulation_stale',
  'calculation_unbalanced',
];

export const CSV_LIMIT = 2 * 1024 * 1024;

export const CSV_COLUMNS: Record<CsvEntity, { required: string[]; optional: string[] }> = {
  sales: {
    required: ['season_id', 'period_start', 'period_end', 'gross_amount'],
    optional: [
      'product',
      'channel',
      'media',
      'sale_type',
      'territory',
      'units',
      'net_amount',
      'currency',
    ],
  },
  shares: {
    required: ['party_name', 'value'],
    optional: [
      'basis',
      'tier',
      'territory',
      'media',
      'notes',
      'role',
      'sale_type',
      'is_ai_revenue',
      'license_start',
      'license_end',
      'advance',
      'deficit',
      'sua',
      'sua_eligible_from',
      'level',
      'is_performer',
      'is_writer',
      'distribution_commission_pct',
      'distribution_expense_pct',
    ],
  },
  residuals: {
    required: ['residual_type'],
    optional: ['party_name', 'rate', 'amount', 'currency', 'effective_from', 'notes'],
  },
  costs: {
    required: ['description', 'recoupability', 'amount', 'recovery_start'],
    optional: [
      'season_id',
      'category',
      'currency',
      'recovery_end',
      'territory',
      'media',
      'sale_type',
    ],
  },
};

export function issue(
  reason: ValidationReason,
  message: string,
  entity: string,
  path: string,
  source: ValidationIssue['source'] = null,
  context: Record<string, unknown> = {},
): ValidationIssue {
  return { reason, message, entity, path, source, context } as ValidationIssue;
}

const normalizeHeader = (value: string) =>
  value
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase();

export function inspectCsvHeader(
  text: string,
  entity: CsvEntity,
): { headers: string[]; issues: ValidationIssue[] } {
  if (new TextEncoder().encode(text).length > CSV_LIMIT)
    return {
      headers: [],
      issues: [issue('csv_limit_exceeded', 'CSV exceeds the 2 MB file limit', entity, '')],
    };

  let first: { record: string[]; info: { empty_lines: number; lines: number } } | undefined;
  try {
    first = (
      parse(text, {
        bom: true,
        trim: true,
        skip_empty_lines: true,
        columns: false,
        info: true,
        relax_column_count: true,
        to: 1,
      }) as unknown as { record: string[]; info: { empty_lines: number; lines: number } }[]
    )[0];
  } catch {
    return {
      headers: [],
      issues: [
        issue(
          'csv_malformed',
          'CSV could not be parsed. Check quotes and column counts.',
          entity,
          '',
        ),
      ],
    };
  }

  if (!first)
    return {
      headers: [],
      issues: [issue('csv_empty', 'CSV contains no header or data', entity, '')],
    };

  const headers = first.record.map(normalizeHeader);
  const source = { record: 1, lineStart: first.info.empty_lines + 1, lineEnd: first.info.lines };
  const issues: ValidationIssue[] = [];
  for (const name of CSV_COLUMNS[entity].required)
    if (!headers.includes(name))
      issues.push(
        issue('csv_missing_header', `Required column ${name} is missing`, entity, name, source),
      );
  for (let index = 0; index < headers.length; index++) {
    const name = headers[index];
    if (!name)
      issues.push(
        issue('csv_malformed', 'Column name cannot be blank', entity, String(index + 1), source),
      );
    else if (headers.indexOf(name) !== index)
      issues.push(
        issue(
          'csv_duplicate_header',
          `Column ${name} appears more than once`,
          entity,
          name,
          source,
        ),
      );
  }
  return { headers, issues };
}
